/**
 * AddChannelWizard.jsx - Multi-step wizard for adding DAW channels
 * 
 * Supports:
 * - Audio Import: Browse storage for audio files
 * - Live Recording: Capture from connected audio devices
 * 
 * Flow:
 * 1. Select channel type (Import/Record)
 * 2. Configure source (file browser or device selection)
 * 3. Preview and confirm
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

// =============================================================================
// WIZARD STEP DEFINITIONS
// =============================================================================

const WIZARD_STEPS = {
  SELECT_TYPE: 'select_type',
  AUDIO_IMPORT: 'audio_import',
  LIVE_RECORD: 'live_record',
  MIDI_SETUP: 'midi_setup',
  VIRTUAL_INSTRUMENT: 'virtual_instrument',
  YOUTUBE_IMPORT: 'youtube_import',
  PREVIEW: 'preview',
  RECORDING: 'recording'
};

const CHANNEL_TYPES = {
  IMPORT: {
    id: 'import',
    label: 'Audio Import',
    description: 'Import audio files from your device',
    icon: '📁',
    color: 'from-blue-500 to-cyan-500'
  },
  YOUTUBE: {
    id: 'youtube',
    label: 'YouTube Import',
    description: 'Extract audio from YouTube video URL',
    icon: '📺',
    color: 'from-red-600 to-red-500'
  },
  RECORD: {
    id: 'record',
    label: 'Live Recording',
    description: 'Record from microphone or audio interface',
    icon: '🎤',
    color: 'from-red-500 to-pink-500'
  },
  MIDI: {
    id: 'midi',
    label: 'MIDI Controller',
    description: 'Connect a MIDI keyboard or controller',
    icon: '🎹',
    color: 'from-purple-500 to-indigo-500'
  },
  VIRTUAL_INSTRUMENT: {
    id: 'virtual',
    label: 'Virtual Instrument',
    description: 'Software synth, sampler, or drum machine',
    icon: '🎛️',
    color: 'from-emerald-500 to-teal-500'
  }
};
// =============================================================================
// AUDIO DEVICE MANAGER HOOK
// =============================================================================

const useAudioDevices = () => {
  const [inputDevices, setInputDevices] = useState([]);
  const [outputDevices, setOutputDevices] = useState([]);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [error, setError] = useState(null);

  const requestPermission = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');
      return true;
    } catch (err) {
      console.error('Audio permission denied:', err);
      setPermissionStatus('denied');
      setError(err.message);
      return false;
    }
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      setInputDevices(inputs);
      setOutputDevices(outputs);
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    // Listen for device changes
    const handleDeviceChange = () => loadDevices();
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [loadDevices]);

  return {
    inputDevices,
    outputDevices,
    permissionStatus,
    error,
    requestPermission,
    loadDevices
  };
};

// =============================================================================
// AUDIO RECORDER HOOK (Enhanced)
// =============================================================================

const useChannelRecorder = (deviceId) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [error, setError] = useState(null);
  const [waveformData, setWaveformData] = useState([]);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setAudioBlob(null);
      setAudioBuffer(null);
      setWaveformData([]);
      
      // Use 'ideal' instead of 'exact' to prevent NotFoundError when device is unavailable
      // If no deviceId, just request any audio device
      const constraints = {
        audio: deviceId && deviceId !== '' && deviceId !== 'default'
          ? { deviceId: { ideal: deviceId } }
          : true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // Setup analyser for live waveform
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Visualize waveform
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const visualize = () => {
        if (!isRecording && !mediaRecorderRef.current) return;
        analyser.getByteTimeDomainData(dataArray);
        setWaveformData([...dataArray]);
        animationRef.current = requestAnimationFrame(visualize);
      };
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        setRecordingTime((Date.now() - startTime) / 1000);
      }, 100);
      
      visualize();
      
    } catch (err) {
      console.error('Recording failed:', err);
      setError(err.message);
    }
  }, [deviceId]);

  const stopRecording = useCallback(async () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(null);
        return;
      }
      
      mediaRecorderRef.current.onstop = async () => {
        clearInterval(timerRef.current);
        cancelAnimationFrame(animationRef.current);
        
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        
        // Convert to AudioBuffer
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const buffer = await audioContext.decodeAudioData(arrayBuffer);
          setAudioBuffer(buffer);
          
          // Stop stream tracks
          streamRef.current?.getTracks().forEach(t => t.stop());
          
          setIsRecording(false);
          resolve(buffer);
        } catch (err) {
          console.error('Failed to decode recording:', err);
          setError(err.message);
          resolve(null);
        }
      };
      
      mediaRecorderRef.current.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    cancelAnimationFrame(animationRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setAudioBuffer(null);
    setWaveformData([]);
  }, [isRecording]);

  return {
    isRecording,
    recordingTime,
    audioBlob,
    audioBuffer,
    waveformData,
    error,
    startRecording,
    stopRecording,
    cancelRecording
  };
};

// =============================================================================
// WIZARD STEP COMPONENTS
// =============================================================================

const StepSelectType = ({ onSelect }) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      <h2 className="text-xl font-bold mb-2">Add New Channel</h2>
      <p className="text-gray-400 text-sm">Choose how you want to add audio to your project</p>
    </div>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Object.values(CHANNEL_TYPES).map(type => (
        <button
          key={type.id}
          onClick={() => onSelect(type.id)}
          className={`group p-6 rounded-xl border-2 border-gray-700 hover:border-purple-500 bg-gradient-to-br ${type.color}/10 hover:${type.color}/20 transition-all duration-300`}
        >
          <div className="text-4xl mb-3">{type.icon}</div>
          <h3 className="text-lg font-bold mb-1">{type.label}</h3>
          <p className="text-sm text-gray-400">{type.description}</p>
        </button>
      ))}
    </div>
  </div>
);

const StepAudioImport = ({ onFileSelected, onBack }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('audio/')) {
      alert('Please select an audio file');
      return;
    }
    
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;
    
    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      onFileSelected({
        file: selectedFile,
        buffer: audioBuffer,
        name: selectedFile.name.replace(/\.[^/.]+$/, ''),
        type: 'import'
      });
    } catch (err) {
      alert('Failed to decode audio file: ' + err.message);
    }
  }, [selectedFile, onFileSelected]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={onBack}
          className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold">Import Audio File</h2>
      </div>
      
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${dragActive 
            ? 'border-purple-500 bg-purple-500/10' 
            : 'border-gray-600 hover:border-purple-500 hover:bg-gray-800/50'
          }
        `}
      >
        <div className="text-4xl mb-3">📁</div>
        <p className="text-lg font-medium mb-2">
          {selectedFile ? selectedFile.name : 'Drop audio file here'}
        </p>
        <p className="text-sm text-gray-400">
          or click to browse
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Supports WAV, MP3, OGG, FLAC, M4A, AIFF
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
        />
      </div>
      
      {selectedFile && (
        <div className="bg-gray-800 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎵</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{selectedFile.name}</p>
              <p className="text-sm text-gray-400">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          
          {previewUrl && (
            <audio 
              src={previewUrl} 
              controls 
              className="w-full h-10"
            />
          )}
          
          <button
            onClick={handleConfirm}
            className="w-full py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold"
          >
            Add to Project
          </button>
        </div>
      )}
    </div>
  );
};

const StepLiveRecord = ({ onRecordingComplete, onBack }) => {
  const devices = useAudioDevices();
  const [selectedDevice, setSelectedDevice] = useState('');
  const [channelName, setChannelName] = useState('Recording');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const recorder = useChannelRecorder(selectedDevice);
  
  // Auto-request permission on mount - but this requires user gesture
  // We'll show a button instead
  const hasAttemptedPermission = useRef(false);
  
  // Load devices if we already have permission
  useEffect(() => {
    // Check if we already have permission by trying to enumerate
    const checkExistingPermission = async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const hasLabels = devs.some(d => d.kind === 'audioinput' && d.label);
        if (hasLabels) {
          // We have permission already
          await devices.loadDevices();
        }
      } catch (e) {
        // No permission yet
      }
    };
    checkExistingPermission();
  }, []);
  
  const handleRequestPermission = async () => {
    setIsRequestingPermission(true);
    try {
      const granted = await devices.requestPermission();
      if (granted) {
        await devices.loadDevices();
      }
    } finally {
      setIsRequestingPermission(false);
      hasAttemptedPermission.current = true;
    }
  };

  // Set default device when devices load
  useEffect(() => {
    if (devices.inputDevices.length > 0 && !selectedDevice) {
      setSelectedDevice(devices.inputDevices[0].deviceId);
    }
  }, [devices.inputDevices, selectedDevice]);

  const handleStartRecording = useCallback(async () => {
    await recorder.startRecording();
  }, [recorder]);

  const handleStopRecording = useCallback(async () => {
    const buffer = await recorder.stopRecording();
    if (buffer) {
      onRecordingComplete({
        buffer,
        name: channelName || `Recording ${Date.now()}`,
        type: 'record',
        deviceId: selectedDevice
      });
    }
  }, [recorder, channelName, selectedDevice, onRecordingComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show permission request screen if no devices loaded yet
  if (devices.inputDevices.length === 0 && devices.permissionStatus !== 'denied') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">
            ← Back
          </button>
          <h2 className="text-xl font-bold">Live Recording</h2>
        </div>
        
        <div className="bg-purple-500/10 border border-purple-500/50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎤</div>
          <h3 className="text-lg font-bold mb-2">Enable Microphone Access</h3>
          <p className="text-sm text-gray-400 mb-4">
            Click the button below to allow microphone access for recording.
          </p>
          <button
            onClick={handleRequestPermission}
            disabled={isRequestingPermission}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {isRequestingPermission ? (
              <>
                <span className="animate-spin">⏳</span>
                Requesting Access...
              </>
            ) : (
              <>
                <span>🎤</span>
                Allow Microphone Access
              </>
            )}
          </button>
          {devices.error && (
            <p className="text-red-400 text-sm mt-4">
              Error: {devices.error}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  if (devices.permissionStatus === 'denied') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">
            ← Back
          </button>
          <h2 className="text-xl font-bold">Microphone Access Denied</h2>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎤❌</div>
          <h3 className="text-lg font-bold mb-2">Permission Required</h3>
          <p className="text-sm text-gray-400 mb-4">
            Please allow microphone access in your browser settings to record audio.
          </p>
          <button
            onClick={handleRequestPermission}
            disabled={isRequestingPermission}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium disabled:opacity-50"
          >
            {isRequestingPermission ? 'Requesting...' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={onBack}
          disabled={recorder.isRecording}
          className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold">Live Recording</h2>
      </div>
      
      {/* Channel Name */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Channel Name</label>
        <input
          type="text"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          disabled={recorder.isRecording}
          placeholder="Enter channel name"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
        />
      </div>
      
      {/* Device Selection */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Recording Device</label>
        <select
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
          disabled={recorder.isRecording}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
        >
          {devices.inputDevices.length === 0 ? (
            <option value="">Loading devices...</option>
          ) : (
            devices.inputDevices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
              </option>
            ))
          )}
        </select>
      </div>
      
      {/* Recording Visualization */}
      <div className="bg-gray-800 rounded-lg p-4">
        {/* Waveform Display */}
        <div className="h-24 bg-gray-900 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
          {recorder.isRecording ? (
            <div className="flex items-end gap-0.5 h-full w-full px-2 py-4">
              {recorder.waveformData.map((value, i) => (
                <div
                  key={i}
                  className="flex-1 bg-purple-500 min-w-[2px] transition-all duration-75"
                  style={{ height: `${((value - 128) / 128) * 100 + 50}%` }}
                />
              ))}
            </div>
          ) : recorder.audioBuffer ? (
            <div className="text-green-400 text-center">
              <span className="text-2xl">✓</span>
              <p className="text-sm">Recording complete</p>
            </div>
          ) : (
            <span className="text-gray-500">Ready to record</span>
          )}
        </div>
        
        {/* Recording Time */}
        <div className="text-center mb-4">
          <span className={`text-3xl font-mono ${recorder.isRecording ? 'text-red-500' : 'text-gray-400'}`}>
            {formatTime(recorder.recordingTime)}
          </span>
        </div>
        
        {/* Controls */}
        <div className="flex gap-3 justify-center">
          {!recorder.isRecording && !recorder.audioBuffer && (
            <button
              onClick={handleStartRecording}
              disabled={!selectedDevice}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 rounded-full font-bold disabled:opacity-50"
            >
              <span className="w-3 h-3 rounded-full bg-white"></span>
              Start Recording
            </button>
          )}
          
          {recorder.isRecording && (
            <button
              onClick={handleStopRecording}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-full font-bold"
            >
              <span className="w-3 h-3 bg-white"></span>
              Stop Recording
            </button>
          )}
          
          {recorder.audioBuffer && !recorder.isRecording && (
            <>
              <button
                onClick={recorder.cancelRecording}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-full font-medium"
              >
                🔄 Re-record
              </button>
              <button
                onClick={handleStopRecording}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-full font-bold"
              >
                ✓ Use Recording
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Error Display */}
      {(devices.error || recorder.error) && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-sm text-red-400">
          {devices.error || recorder.error}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MIDI SETUP STEP
// =============================================================================

const useMIDIDevices = () => {
  const [midiInputs, setMidiInputs] = useState([]);
  const [midiOutputs, setMidiOutputs] = useState([]);
  const [midiAccess, setMidiAccess] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('requesting'); // 'requesting', 'granted', 'denied', 'unsupported'

  const requestAccess = useCallback(async () => {
    if (!navigator.requestMIDIAccess) {
      setStatus('unsupported');
      setError('Web MIDI API is not supported in this browser');
      return false;
    }

    try {
      setError(null);
      setStatus('requesting');
      const access = await navigator.requestMIDIAccess({ sysex: false });
      setMidiAccess(access);
      setStatus('granted');
      
      // Get initial devices
      const inputs = Array.from(access.inputs.values());
      const outputs = Array.from(access.outputs.values());
      setMidiInputs(inputs);
      setMidiOutputs(outputs);
      
      // Listen for device changes
      access.onstatechange = (e) => {
        setMidiInputs(Array.from(access.inputs.values()));
        setMidiOutputs(Array.from(access.outputs.values()));
      };
      
      return true;
    } catch (err) {
      console.error('MIDI access denied:', err);
      setStatus('denied');
      setError(err.message);
      return false;
    }
  }, []);

  return {
    midiInputs,
    midiOutputs,
    midiAccess,
    error,
    status,
    requestAccess
  };
};

const StepMIDISetup = ({ onDeviceSelected, onBack }) => {
  const midi = useMIDIDevices();
  const [selectedInput, setSelectedInput] = useState('');
  const [channelName, setChannelName] = useState('MIDI Track');
  const [midiNotes, setMidiNotes] = useState([]);

  useEffect(() => {
    midi.requestAccess();
  }, []);

  // Listen for MIDI notes when device is selected
  useEffect(() => {
    if (!midi.midiAccess || !selectedInput) return;

    const input = midi.midiInputs.find(i => i.id === selectedInput);
    if (!input) return;

    const handleMIDIMessage = (message) => {
      const [status, note, velocity] = message.data;
      // Note on (0x90) with velocity > 0
      if ((status & 0xF0) === 0x90 && velocity > 0) {
        setMidiNotes(prev => [...prev.slice(-20), { note, velocity, time: Date.now() }]);
      }
    };

    input.onmidimessage = handleMIDIMessage;
    return () => { input.onmidimessage = null; };
  }, [midi.midiAccess, midi.midiInputs, selectedInput]);

  const handleConfirm = () => {
    const input = midi.midiInputs.find(i => i.id === selectedInput);
    onDeviceSelected({
      name: channelName || `MIDI ${input?.name || 'Track'}`,
      type: 'midi',
      midiInputId: selectedInput,
      midiInputName: input?.name || 'Unknown'
    });
  };

  if (midi.status === 'unsupported') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">← Back</button>
          <h2 className="text-xl font-bold">MIDI Not Supported</h2>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎹❌</div>
          <h3 className="text-lg font-bold mb-2">Browser Limitation</h3>
          <p className="text-sm text-gray-400">
            Web MIDI API is not supported. Try Chrome, Edge, or Opera for MIDI support.
          </p>
        </div>
      </div>
    );
  }

  if (midi.status === 'denied') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">← Back</button>
          <h2 className="text-xl font-bold">MIDI Access Denied</h2>
        </div>
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎹❌</div>
          <h3 className="text-lg font-bold mb-2">Permission Required</h3>
          <p className="text-sm text-gray-400 mb-4">
            Please allow MIDI access in your browser settings.
          </p>
          <button onClick={midi.requestAccess} className="px-6 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">← Back</button>
        <h2 className="text-xl font-bold">MIDI Controller Setup</h2>
      </div>

      {/* Channel Name */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Track Name</label>
        <input
          type="text"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          placeholder="Enter track name"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
        />
      </div>

      {/* Device Selection */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">MIDI Input Device</label>
        <select
          value={selectedInput}
          onChange={(e) => setSelectedInput(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
        >
          <option value="">Select MIDI device...</option>
          {midi.midiInputs.map(device => (
            <option key={device.id} value={device.id}>
              {device.name || `MIDI Input ${device.id.slice(0, 8)}`}
            </option>
          ))}
        </select>
        {midi.midiInputs.length === 0 && (
          <p className="text-xs text-yellow-500 mt-1">No MIDI devices detected. Connect a device and refresh.</p>
        )}
      </div>

      {/* MIDI Activity Monitor */}
      {selectedInput && (
        <div className="bg-gray-800 rounded-lg p-4">
          <label className="block text-sm text-gray-400 mb-2">MIDI Activity (play notes to test)</label>
          <div className="h-16 bg-gray-900 rounded flex items-end gap-0.5 px-2">
            {midiNotes.slice(-40).map((n, i) => (
              <div
                key={i}
                className="w-2 bg-purple-500 rounded-t transition-all"
                style={{ height: `${(n.velocity / 127) * 100}%`, opacity: 1 - (i * 0.02) }}
              />
            ))}
            {midiNotes.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-gray-600 text-xs pb-6">
                Waiting for MIDI input...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Button */}
      <button
        onClick={handleConfirm}
        disabled={!selectedInput}
        className="w-full py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Add MIDI Track
      </button>
    </div>
  );
};

// =============================================================================
// VIRTUAL INSTRUMENT STEP
// =============================================================================

const VIRTUAL_INSTRUMENTS = [
  {
    id: 'synth-basic',
    name: 'Basic Synth',
    description: 'Simple oscillator-based synthesizer',
    icon: '🎹',
    category: 'Synth',
    params: { type: 'sawtooth', attack: 0.01, release: 0.3 }
  },
  {
    id: 'synth-pad',
    name: 'Ambient Pad',
    description: 'Lush pads with reverb',
    icon: '🌊',
    category: 'Synth',
    params: { type: 'sine', attack: 0.5, release: 2.0 }
  },
  {
    id: 'synth-bass',
    name: 'Sub Bass',
    description: 'Deep bass synth',
    icon: '🔊',
    category: 'Bass',
    params: { type: 'sine', attack: 0.01, release: 0.5 }
  },
  {
    id: 'drums-808',
    name: '808 Drums',
    description: 'Classic TR-808 style kit',
    icon: '🥁',
    category: 'Drums',
    params: { kit: '808' }
  },
  {
    id: 'drums-acoustic',
    name: 'Acoustic Kit',
    description: 'Natural acoustic drum sounds',
    icon: '🪘',
    category: 'Drums',
    params: { kit: 'acoustic' }
  },
  {
    id: 'sampler',
    name: 'Sampler',
    description: 'Load and play audio samples',
    icon: '📼',
    category: 'Sampler',
    params: { type: 'sampler' }
  }
];

// =============================================================================
// YOUTUBE IMPORT STEP
// =============================================================================

const StepYouTubeImport = ({ onAudioExtracted, onBack }) => {
  const [url, setUrl] = useState('');
  const [videoInfo, setVideoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [channelName, setChannelName] = useState('');

  // Validate YouTube URL pattern
  const isValidUrl = (input) => {
    const patterns = [
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/,
      /^(https?:\/\/)?(music\.youtube\.com)\/.+$/
    ];
    return patterns.some(pattern => pattern.test(input));
  };

  const handleFetchInfo = async () => {
    if (!url.trim() || !isValidUrl(url)) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    setLoading(true);
    setError(null);
    setVideoInfo(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.aiiai.org'}/api/youtube/info?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to get video info');
      }

      setVideoInfo(data.info);
      setChannelName(data.info.title || 'YouTube Audio');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExtract = async () => {
    if (!videoInfo) return;

    setExtracting(true);
    setError(null);

    try {
      // Request audio extraction
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.aiiai.org'}/api/youtube/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, format: 'mp3', quality: '192' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to extract audio');
      }

      // Download the audio file
      const downloadUrl = `${import.meta.env.VITE_API_URL || 'https://api.aiiai.org'}${data.downloadUrl}`;
      const audioResponse = await fetch(downloadUrl);
      const audioBlob = await audioResponse.blob();

      // Decode to AudioBuffer
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      onAudioExtracted({
        buffer: audioBuffer,
        name: channelName || videoInfo.title || 'YouTube Audio',
        type: 'import',
        source: 'youtube',
        sourceUrl: url,
        sourceTitle: videoInfo.title
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setExtracting(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">← Back</button>
        <h2 className="text-xl font-bold">Import from YouTube</h2>
      </div>

      {/* URL Input */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">YouTube URL</label>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtu.be/... or https://youtube.com/watch?v=..."
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500"
            disabled={loading || extracting}
          />
          <button
            onClick={handleFetchInfo}
            disabled={!url.trim() || loading || extracting}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium disabled:opacity-50"
          >
            {loading ? '...' : '🔍'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Paste a YouTube or YouTube Music link
        </p>
      </div>

      {/* Video Preview */}
      {videoInfo && (
        <div className="bg-gray-800 rounded-lg p-4 space-y-3">
          <div className="flex gap-4">
            {videoInfo.thumbnail && (
              <img 
                src={videoInfo.thumbnail} 
                alt="Thumbnail" 
                className="w-32 h-20 object-cover rounded"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm truncate">{videoInfo.title}</h3>
              <p className="text-xs text-gray-400">{videoInfo.channel}</p>
              <div className="flex gap-3 mt-1 text-xs text-gray-500">
                <span>⏱️ {videoInfo.durationString || formatDuration(videoInfo.duration)}</span>
                {videoInfo.viewCount && <span>👁️ {videoInfo.viewCount.toLocaleString()}</span>}
              </div>
            </div>
          </div>

          {/* Channel Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Track Name</label>
            <input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder={videoInfo.title}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Extract Button */}
          <button
            onClick={handleExtract}
            disabled={extracting}
            className="w-full py-3 bg-red-600 hover:bg-red-500 rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {extracting ? (
              <>
                <span className="animate-spin">⏳</span>
                Extracting Audio...
              </>
            ) : (
              <>
                <span>🎵</span>
                Extract Audio
              </>
            )}
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Info */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>⚠️ Only extract audio from content you have rights to use.</p>
        <p>📋 Supported: YouTube, YouTube Music, YouTube Shorts</p>
      </div>
    </div>
  );
};

const StepVirtualInstrument = ({ onInstrumentSelected, onBack }) => {
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [channelName, setChannelName] = useState('');
  const [category, setCategory] = useState('all');

  const categories = ['all', ...new Set(VIRTUAL_INSTRUMENTS.map(i => i.category))];
  const filtered = category === 'all' 
    ? VIRTUAL_INSTRUMENTS 
    : VIRTUAL_INSTRUMENTS.filter(i => i.category === category);

  const handleConfirm = () => {
    if (!selectedInstrument) return;
    onInstrumentSelected({
      name: channelName || selectedInstrument.name,
      type: 'virtual',
      instrumentId: selectedInstrument.id,
      instrumentName: selectedInstrument.name,
      instrumentParams: selectedInstrument.params
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600">← Back</button>
        <h2 className="text-xl font-bold">Virtual Instrument</h2>
      </div>

      {/* Track Name */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Track Name</label>
        <input
          type="text"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          placeholder={selectedInstrument?.name || 'Enter track name'}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
        />
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
              category === cat 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Instrument Grid */}
      <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
        {filtered.map(instrument => (
          <button
            key={instrument.id}
            onClick={() => setSelectedInstrument(instrument)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              selectedInstrument?.id === instrument.id
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-gray-700 bg-gray-800 hover:border-gray-600'
            }`}
          >
            <div className="text-2xl mb-2">{instrument.icon}</div>
            <h4 className="font-bold text-sm">{instrument.name}</h4>
            <p className="text-xs text-gray-400 mt-1">{instrument.description}</p>
          </button>
        ))}
      </div>

      {/* Confirm Button */}
      <button
        onClick={handleConfirm}
        disabled={!selectedInstrument}
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Add Virtual Instrument
      </button>
    </div>
  );
};

// =============================================================================
// MAIN WIZARD COMPONENT
// =============================================================================

export const AddChannelWizard = ({ isOpen, onClose, onChannelAdded }) => {
  const [currentStep, setCurrentStep] = useState(WIZARD_STEPS.SELECT_TYPE);

  const handleTypeSelect = useCallback((type) => {
    if (type === 'import') {
      setCurrentStep(WIZARD_STEPS.AUDIO_IMPORT);
    } else if (type === 'youtube') {
      setCurrentStep(WIZARD_STEPS.YOUTUBE_IMPORT);
    } else if (type === 'record') {
      setCurrentStep(WIZARD_STEPS.LIVE_RECORD);
    } else if (type === 'midi') {
      setCurrentStep(WIZARD_STEPS.MIDI_SETUP);
    } else if (type === 'virtual') {
      setCurrentStep(WIZARD_STEPS.VIRTUAL_INSTRUMENT);
    }
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep(WIZARD_STEPS.SELECT_TYPE);
  }, []);

  const handleChannelCreated = useCallback((channelData) => {
    onChannelAdded?.(channelData);
    setCurrentStep(WIZARD_STEPS.SELECT_TYPE);
    onClose?.();
  }, [onChannelAdded, onClose]);

  const handleClose = useCallback(() => {
    setCurrentStep(WIZARD_STEPS.SELECT_TYPE);
    onClose?.();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎵</span>
            <span className="font-bold">Channel Wizard</span>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {currentStep === WIZARD_STEPS.SELECT_TYPE && (
            <StepSelectType onSelect={handleTypeSelect} />
          )}
          
          {currentStep === WIZARD_STEPS.AUDIO_IMPORT && (
            <StepAudioImport 
              onFileSelected={handleChannelCreated}
              onBack={handleBack}
            />
          )}

          {currentStep === WIZARD_STEPS.YOUTUBE_IMPORT && (
            <StepYouTubeImport
              onAudioExtracted={handleChannelCreated}
              onBack={handleBack}
            />
          )}
          
          {currentStep === WIZARD_STEPS.LIVE_RECORD && (
            <StepLiveRecord
              onRecordingComplete={handleChannelCreated}
              onBack={handleBack}
            />
          )}

          {currentStep === WIZARD_STEPS.MIDI_SETUP && (
            <StepMIDISetup
              onDeviceSelected={handleChannelCreated}
              onBack={handleBack}
            />
          )}

          {currentStep === WIZARD_STEPS.VIRTUAL_INSTRUMENT && (
            <StepVirtualInstrument
              onInstrumentSelected={handleChannelCreated}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddChannelWizard;
